/**
 * <evo-chart>: animated line chart (sections/evo-decline-chart.liquid).
 * The curves are generated from published rates (see the section comment) for the configured
 * age range; the y-axis is fitted to the data so the gap between the curves is readable.
 *   white  = 100 % − loss(age)               (loss: data-loss-per-year from data-peak-age,
 *                                              data-late-loss-per-year from data-late-age)
 *   yellow = 100 % − loss(age) × (1 − data-protein-reduction / 100)
 * Curves draw in once the chart scrolls into view. Plot geometry comes from data attributes so
 * Liquid (axes, titles) and JS (grid, ticks, curves, labels) share the same coordinates.
 */
class EvoChart extends HTMLElement {
  connectedCallback() {
    const d = this.dataset;
    this.geo = {
      x0: parseFloat(d.x0),
      x1: parseFloat(d.x1),
      y0: parseFloat(d.y0),
      y1: parseFloat(d.y1),
    };
    this.ageFrom = parseFloat(d.ageFrom);
    this.ageTo = parseFloat(d.ageTo);
    this.peakAge = parseFloat(d.peakAge);
    this.lossPerYear = parseFloat(d.lossPerYear);
    this.lateAge = parseFloat(d.lateAge);
    this.lateLossPerYear = parseFloat(d.lateLossPerYear);
    this.proteinReduction = parseFloat(d.proteinReduction) || 0;
    this.deltaLabel = d.deltaLabel || "";
    this.svgNs = "http://www.w3.org/2000/svg";

    if (this.ageTo > this.ageFrom) this.build();

    const reveal = () => this.classList.add("is-visible");
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      reveal();
      return;
    }
    new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal();
          observer.disconnect();
        });
      },
      { threshold: 0.35 }
    ).observe(this);
  }

  // cumulative loss in % of peak mass at a given age
  loss(age) {
    let total = 0;
    if (age > this.peakAge) total += (Math.min(age, this.lateAge) - this.peakAge) * this.lossPerYear;
    if (age > this.lateAge) total += (age - this.lateAge) * this.lateLossPerYear;
    return total;
  }

  el(name, attrs, text) {
    const node = document.createElementNS(this.svgNs, name);
    Object.keys(attrs).forEach((key) => node.setAttribute(key, attrs[key]));
    if (text !== undefined) node.textContent = text;
    return node;
  }

  format(loss) {
    return "−" + loss.toFixed(1).replace(".", ",") + " %";
  }

  build() {
    const g = this.geo;
    const a0 = this.ageFrom;
    const a1 = this.ageTo;
    const factor = 1 - this.proteinReduction / 100;
    const steps = 48;

    const typical = [];
    const protein = [];
    for (let i = 0; i <= steps; i++) {
      const age = a0 + ((a1 - a0) * i) / steps;
      const loss = this.loss(age);
      typical.push({ age, pct: 100 - loss });
      protein.push({ age, pct: 100 - loss * factor });
    }

    // y-axis: from 100 % down to just below the lowest point, on a "nice" step
    const pctMin = typical[typical.length - 1].pct;
    const span = Math.max(100 - pctMin, 0.5);
    const step = span <= 5 ? 1 : span <= 12 ? 2 : span <= 30 ? 5 : 10;
    const lo = Math.floor((pctMin - span * 0.18) / step) * step;
    const hi = 100;

    const sx = (age) => g.x0 + ((age - a0) / (a1 - a0)) * (g.x1 - g.x0);
    const sy = (pct) => g.y0 - ((pct - lo) / (hi - lo)) * (g.y0 - g.y1);

    // grid + y ticks
    const grid = this.querySelector("[data-grid]");
    const ticks = this.querySelector("[data-ticks]");
    grid.innerHTML = "";
    ticks.innerHTML = "";
    for (let v = lo; v <= hi + 0.001; v += step) {
      const y = sy(v);
      if (v > lo) grid.appendChild(this.el("line", { class: "evo-chart__grid", x1: g.x0, x2: g.x1, y1: y, y2: y }));
      ticks.appendChild(this.el("text", { class: "evo-chart__tick", x: g.x0 - 10, y: y + 4, "text-anchor": "end" }, v + " %"));
    }

    // x ticks
    const xStep = a1 - a0 <= 25 ? 5 : 10;
    for (let age = Math.ceil(a0 / xStep) * xStep; age <= a1; age += xStep) {
      ticks.appendChild(this.el("text", { class: "evo-chart__tick", x: sx(age), y: g.y0 + 22, "text-anchor": "middle" }, String(age)));
    }
    if (a0 % xStep !== 0) ticks.appendChild(this.el("text", { class: "evo-chart__tick", x: sx(a0), y: g.y0 + 22, "text-anchor": "middle" }, String(a0)));
    if (a1 % xStep !== 0) ticks.appendChild(this.el("text", { class: "evo-chart__tick", x: sx(a1), y: g.y0 + 22, "text-anchor": "middle" }, String(a1)));

    // curves + area
    const toPath = (points) => points.map((p, i) => (i === 0 ? "M" : "L") + sx(p.age).toFixed(1) + " " + sy(p.pct).toFixed(1)).join(" ");
    const typicalPath = this.querySelector("[data-chart-curve]");
    const proteinPath = this.querySelector("[data-chart-curve-2]");
    const area = this.querySelector("[data-chart-area]");
    typicalPath.setAttribute("d", toPath(typical));
    typicalPath.setAttribute("pathLength", "1");
    if (proteinPath) {
      proteinPath.setAttribute("d", toPath(protein));
      proteinPath.setAttribute("pathLength", "1");
    }
    if (area) area.setAttribute("d", toPath(typical) + " L" + g.x1 + " " + g.y0 + " L" + g.x0 + " " + g.y0 + " Z");

    // end dots; with two curves a bracket between them and the relative difference
    // ("+67 %" = how much more muscle the typical curve loses than the protein curve)
    const ends = this.querySelector("[data-ends]");
    ends.innerHTML = "";
    const series = [{ points: typical, accent: false }];
    if (proteinPath) series.push({ points: protein, accent: true });
    const labels = series.map((entry) => {
      const last = entry.points[entry.points.length - 1];
      return { x: sx(last.age), y: sy(last.pct), loss: 100 - last.pct, accent: entry.accent };
    });
    labels.forEach((label) => {
      ends.appendChild(this.el("circle", { class: "evo-chart__dot" + (label.accent ? " evo-chart__dot--accent" : ""), cx: label.x, cy: label.y, r: 6 }));
    });

    if (labels.length === 2 && labels[1].loss > 0) {
      const upper = labels[0].y < labels[1].y ? labels[0] : labels[1];
      const lower = upper === labels[0] ? labels[1] : labels[0];
      const x = upper.x;
      const gap = lower.y - upper.y;
      if (gap > 18) {
        ends.appendChild(this.el("line", { class: "evo-chart__bracket", x1: x, y1: upper.y + 9, x2: x, y2: lower.y - 9, pathLength: "1" }));
      }
      const ratio = (labels[0].loss - labels[1].loss) / labels[1].loss;
      const pct = "+" + Math.round(ratio * 100) + " %";
      const midY = (upper.y + lower.y) / 2;
      const captionWords = this.deltaLabel.split(/\s+/).filter(Boolean);
      // wrap the caption at ~12 characters per line
      const lines = [];
      captionWords.forEach((word) => {
        if (lines.length && (lines[lines.length - 1] + " " + word).length <= 15) lines[lines.length - 1] += " " + word;
        else lines.push(word);
      });
      const blockH = 22 + lines.length * 12;
      let y = midY - blockH / 2 + 18;
      ends.appendChild(this.el("text", { class: "evo-chart__value evo-chart__delta", x: x + 16, y, "text-anchor": "start" }, pct));
      lines.forEach((line) => {
        y += 13;
        ends.appendChild(this.el("text", { class: "evo-chart__value evo-chart__delta-caption", x: x + 16, y, "text-anchor": "start" }, line));
      });
    }
  }
}

if (!window.customElements.get("evo-chart")) {
  window.customElements.define("evo-chart", EvoChart);
}
